<template>
    <b-container fluid class="top-padding bottom-padding no-horiz-padding">

    <h2>Digital Archive on Weaving, Textiles, Lace, and Related Topics</h2>
    <hr />
    <h3 class='inline-auto-width' v-if="(searchType==='keyword')">Search by Keyword</h3>
    <h3 class='inline-auto-width' v-if="(searchType==='topic')">Search by Topic</h3>
    <h3 class='inline-auto-width' v-if="(searchType==='author')">Search by Author</h3>
    <h3 class='inline-auto-width' v-if="(searchType==='language')">Search by Language</h3>
    <h3 class='inline-auto-width' v-if="(searchType==='publication')">Search by Publication</h3>
    <share-button/>
    <hr />
    <p>
        All documents in the archive are shown below by default; use the controls here to search for specific items.
    </p>

    <div v-if="(searchType==='keyword')">
        Enter Search Term(s):<br />
        <div class="input-group">
            <b-form-input type="text" v-model='keyword' @input='debounceKeywordInput'/>
            <span class="input-group-btn">
                <button class="btn btn-default" @click='loadMatchingDocuments' type="button">Search</button>
            </span>
        </div>
    </div>

    <div v-if="(searchType==='topic')">
        Select a Topic:<br />
        <b-form-select v-model="selectedTopicId" :options='topicOptions' @change='setSelectedItem'/>
    </div>

    <div v-if="(searchType==='language')">
        Select a Language:<br />
        <b-form-select v-model="selectedLanguageId" :options='languageOptions' @change='setSelectedItem'/>
    </div>

    <div v-if="(searchType==='publication')">
        Select a Publication<br />
        <b-form-select v-model="selectedPublicationId" :options='publicationOptions' @change='setSelectedItem'/>
    </div>

    <div v-if="(searchType==='author')">
        Select an Author:<br />
        <b-form-select v-model="selectedAuthorId" :options='authorOptions' @change='setSelectedItem'/>
    </div>

    <document-search-results v-show='resultsVisible()' :documentsList='documentsArray' :items-per-page='itemsPerPage' :current-page-number="this.initialPageNum" :total-results='totalResults' :on-go-to-page="this.goToPage" />

    <div class="top-padding">
        <img :src='getSiteImageUrl("Swift.png")' />
    </div>

    <help-us-button/>
    <mail-chimp-signup/>

    </b-container>
</template>

<script>

import utilityService from '../mixins/utilityService'
import dataService from '../mixins/dataService'
import urlsService from '../mixins/urlsService'
import mediaService from '../mixins/mediaService'
import helpUsButton from './HelpUsButton.vue'
import mailChimpSignup from './MailChimpSignup.vue'
import documentSearchResults from './DocumentSearchResults.vue'
import relatedStoreProducts from './RelatedStoreProducts'
import shareButton from './ShareButton.vue'
import cfg from '../config/config.js'

export default {
    mixins: [utilityService, dataService, mediaService, urlsService],
    data() {
        return {
            keyword: '',
            selectedTopicId: 0,
            selectedLanguageId: '',
            selectedPublicationId: 0,
            selectedAuthorId: 0,
            topicOptions: [],
            languageOptions: [],
            publicationOptions: [],
            authorOptions: [],
            itemsPerPage: 0,
            initialPageNum: 0,
            documentsArray: [],
            totalResults: 0
        }
    },
    methods: {

        getCurrentTopicOption() {
            return this.topicOptions.find(c => c.value === this.selectedTopicId)
        },

        getCurrentLanguageOption() {
            return this.languageOptions.find(c => c.value === this.selectedLanguageId)
        },

        getCurrentPublicationOption() {
            return this.publicationOptions.find(c => c.value === this.selectedPublicationId)
        },

        getCurrentAuthorOption() {
            return this.authorOptions.find(c => c.value === this.selectedAuthorId)
        },

        goToPage(pageNumber) {
            let q = this.getCurrentQuerystringCopy() // vue router won't pick up changes to the original object; a copy must be made
            q.page = pageNumber + ''
            this.$router.replace({ query: q })
            this.initializeComponent()
        },

        getCurrentQuerystringCopy() {
            let q = Object.assign({}, this.$route.query) // vue router won't pick up changes to the original object; a copy must be made
            if (!q.page) {
                q.page = '0'
            }
            return q
        },

        setSelectedItem(newItemId) {
            let p = Object.assign({}, this.$route.params) // vue router won't pick up changes to the original object; a copy must be made

            if (!newItemId) {
                newItemId = 0
            }

            p.searchType = this.searchType
            p.id = newItemId
            let q = this.getCurrentQuerystringCopy()
            q.page = '0'

            if (this.searchType === 'topic') {
                this.selectedTopicId = newItemId
                p.title = (newItemId > 0) ? this.sanitizeUrlSegment(this.getCurrentTopicOption(newItemId).text) : null
            }

            else if (this.searchType === 'language') {
                this.selectedLanguageId = newItemId
                p.title = null
                if (this.selectedLanguageId && this.selectedLanguageID !== 0 && this.selectedLanguageID !== '' && this.selectedLanguageId.length > 0) {
                    p.title = this.sanitizeUrlSegment(this.getCurrentLanguageOption(newItemId).text)
                }
            }

            else if (this.searchType === 'publication') {
                this.selectedPublicationId = newItemId
                p.title = (newItemId > 0) ? this.sanitizeUrlSegment(this.getCurrentPublicationOption(newItemId).text) : null
            }

            else if (this.searchType === 'author') {
                this.selectedAuthorId = newItemId
                p.title = (newItemId > 0) ? this.sanitizeUrlSegment(this.getCurrentAuthorOption(newItemId).text) : null
            }

            else if (this.searchType === 'keyword') {
                this.keyword = newItemId
                p.title = (newItemId > 0) ? this.sanitizeUrlSegment(newItemId) : null
            }

            this.$router.replace({ params: p, query: q })
            this.initializeComponent()
        },

        resultsVisible() {
            if (this.searchType === 'topic') {
                return this.selectedTopicId > 0
            }
            if (this.searchType === 'language') {
                return this.selectedLanguageId && this.selectedLanguageID !== 0 && this.selectedLanguageID !== '' && this.selectedLanguageId.length > 0
            }
            if (this.searchType === 'publication') {
                return this.selectedPublicationId > 0
            }
            if (this.searchType === 'author') {
                return this.selectedAuthorId > 0
            }
            if (this.searchType === 'keyword') {
                return this.keyword.trim().length > 0
            }
            return true
        },

        initializeComponent() {
            this.itemsPerPage = cfg.config.documentResultsPerPage
            this.searchType = this.$route.params.searchType
            this.initialPageNum = this.$route.query.page ? Number.parseInt(this.$route.query.page) : 0

            let selectedOptionId
            if (this.searchType !== 'language') {
                selectedOptionId = Number.parseInt(this.$route.params.id ? this.$route.params.id : 0)
                if (!selectedOptionId) { // can happen because the previous Angular version used "all" as a fake category name when not specified
                    selectedOptionId = 0
                }
            }
            else {
                selectedOptionId = this.$route.params.id
            }

            switch (this.searchType) {
                case 'topic': {
                    if (this.topicOptions.length === 0) {
                        this.loadOptionsList(this.getDATopics, '(Select a Topic)', 'Name', 'ID', true).then(resp => {
                            this.topicOptions = resp
                            this.selectedTopicId = selectedOptionId
                            this.loadMatchingDocuments()
                        })
                    }
                    else {
                        this.loadMatchingDocuments()
                    }
                    break
                }
                case 'language': {
                    if (this.languageOptions.length === 0) {
                        this.loadOptionsList(this.getDALanguages, '(Select a Language)', 'Name', 'Name', false).then(resp => {
                            this.languageOptions = resp
                            this.selectedLanguageId = selectedOptionId
                            this.loadMatchingDocuments()
                        })
                    }
                    else {
                        this.loadMatchingDocuments()
                    }
                    break
                }
                case 'publication': {
                    if (this.publicationOptions.length === 0) {
                        this.loadOptionsList(this.getDAPublications, '(Select a Publication)', 'Name', 'ID', true).then(resp => {
                            this.publicationOptions = resp
                            this.selectedPublicationId = selectedOptionId
                            this.loadMatchingDocuments()
                        })
                    }
                    else {
                        this.loadMatchingDocuments()
                    }
                    break
                }
                case 'author': {
                    if (this.authorOptions.length === 0) {
                        this.loadOptionsList(this.getDAAuthors, '(Select an Author)', 'Name', 'ID', true).then(resp => {
                            this.authorOptions = resp
                            this.selectedAuthorId = selectedOptionId
                            this.loadMatchingDocuments()
                        })
                    }
                    else {
                        this.loadMatchingDocuments()
                    }
                    break
                }
                case 'keyword': {
                    this.loadMatchingDocuments()
                    break
                }
            }
        },

        loadOptionsList(daLoadFunction, selectPrompt, promptFieldName, idFieldName, idIsNumber) {
            return daLoadFunction().then(resp => {
                let optionsList = []
                optionsList.push({ text: selectPrompt, value: 0 })
                for (let c = 0; c < resp.data.length; c++) {
                    let item = resp.data[c]
                    if ((!idIsNumber || this.isValidPositiveOrZeroNumber(item[idFieldName])) && this.hasAtLeastOneAlphaNumericChar(item[promptFieldName])) {
                        optionsList.push({ text: item[promptFieldName], value: item[idFieldName] })
                    }
                }
                return optionsList
            })
        },

        loadMatchingDocuments() {
            let startingResult = this.calculateCurrentStartingResult()
            this.getMatchingDAItems(this.keyword, this.selectedAuthorId, this.selectedPublicationId, this.selectedTopicId, this.selectedLanguageId, '', 'Title', startingResult, this.itemsPerPage).then(resp => {
                this.totalResults = resp.data.TotalResults
                this.documentsArray = resp.data.DAItemsList
            })
        },

        debounceKeywordInput() {
            this.debounceInput(function() {
                this.loadMatchingDocuments()
            }, 1000)
        },

        calculateCurrentStartingResult() {
            return this.initialPageNum * this.itemsPerPage + 1 // paged document functions expect a startingResult that is 1-based, but maintained in Vue component state as 0-based
        }
    },

    components: {
        helpUsButton: helpUsButton,
        mailChimpSignup: mailChimpSignup,
        documentSearchResults: documentSearchResults,
        relatedStoreProducts: relatedStoreProducts,
        shareButton: shareButton
    },

    created() {
        this.goToPage(0) // this will add any missing querystring parameters with default values
        this.$store.commit('setPageTitle', 'Textiles Document Search')
    }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/DocumentSearch.vue